import Features from '../app/tracking/components/DynamicTrackPage/features';

export const NO_MEDIA_ARM = 'NO_MEDIA';

export const GEO_COUNTRY_HEADER = 'x-client-geo-country';
export const GEO_REGION_HEADER = 'x-client-geo-state';

const BLOCKED_LOCALES = new Set(['fr_ca']);
const BLOCKED_REGIONS = new Set(['caqc']);
const INJECTION_LOCALES = new Set(['en_us']);

const REGION_REQUIRED_COUNTRIES = new Set(['ca']);

const SUBDIVISION = /^[a-z]{2}-?[a-z0-9]{1,3}$/;

const normalize = (value: string | null | undefined): string =>
  value?.trim().toLowerCase() ?? '';

const normalizeSubdivision = (value: string | null | undefined): string =>
  normalize(value).replace('-', '');

export function isRetailMediaBlockedLocale(
  locale: string | null | undefined,
): boolean {
  if (!locale) return false;
  const [language, country] = locale.split(/[_-]/);
  if (!language || !country) return false;
  return BLOCKED_LOCALES.has(`${language}_${country}`.toLowerCase());
}

export function isRetailMediaBlockedRegion(
  country: string | null | undefined,
  region: string | null | undefined,
): boolean {
  const normalizedRegion = normalize(region);
  if (SUBDIVISION.test(normalizedRegion)) {
    return BLOCKED_REGIONS.has(normalizeSubdivision(normalizedRegion));
  }

  return REGION_REQUIRED_COUNTRIES.has(normalize(country));
}

const INJECTED_ROW_ID = 'injected-retail-media-row';
const FULL_WIDTH = 12;

interface LayoutNode {
  id?: unknown;
  type?: unknown;
  name?: unknown;
  width?: unknown;
  children?: unknown;
}

export function hasRetailMediaNode(layout: unknown): boolean {
  if (!layout || typeof layout !== 'object') return false;
  const node = layout as LayoutNode;
  if (node.name === Features.RETAIL_MEDIA) return true;
  return Array.isArray(node.children) && node.children.some(hasRetailMediaNode);
}

function childrenOf(node: LayoutNode): LayoutNode[] | null {
  return Array.isArray(node.children) ? node.children : null;
}

function containsEddPanel(node: LayoutNode): boolean {
  return (
    node.name === Features.EDD_PANEL ||
    (childrenOf(node)?.some(containsEddPanel) ?? false)
  );
}

function retailMediaRow(): LayoutNode {
  return {
    id: INJECTED_ROW_ID,
    type: 'Row',
    children: [
      {
        id: `${INJECTED_ROW_ID}-col`,
        type: 'Col',
        width: FULL_WIDTH,
        children: [
          {
            id: `${INJECTED_ROW_ID}-feature`,
            type: 'Feature',
            name: Features.RETAIL_MEDIA,
          },
        ],
      },
    ],
  };
}

function insertBelowEddRow(root: LayoutNode): LayoutNode | null {
  const children = childrenOf(root);
  const index = children?.findIndex(containsEddPanel) ?? -1;
  if (!children || index === -1 || children[index].type !== 'Row') return null;

  return {
    ...root,
    children: [
      ...children.slice(0, index + 1),
      retailMediaRow(),
      ...children.slice(index + 1),
    ],
  };
}

export function injectRetailMediaBelowEdd<T>(layout: T): T {
  if (!layout || typeof layout !== 'object' || hasRetailMediaNode(layout)) {
    return layout;
  }

  return (insertBelowEddRow(layout as LayoutNode) ?? layout) as T;
}

export function canInjectRetailMedia(layout: unknown): boolean {
  return injectRetailMediaBelowEdd(layout) !== layout;
}

interface RetailMediaEligibilityParams {
  retailMediaEnabled: boolean | null | undefined;
  locale: string | null | undefined;
  geoCountry: string | null | undefined;
  geoRegion: string | null | undefined;
  trackPageLayout: unknown;
  skipAnalytics?: boolean;
}

export function isRetailMediaEligible({
  retailMediaEnabled,
  locale,
  geoCountry,
  geoRegion,
  trackPageLayout,
  skipAnalytics = false,
}: RetailMediaEligibilityParams): boolean {
  return (
    !skipAnalytics &&
    retailMediaEnabled === true &&
    !isRetailMediaBlockedLocale(locale) &&
    !isRetailMediaBlockedRegion(geoCountry, geoRegion) &&
    hasRetailMediaNode(trackPageLayout)
  );
}

interface RetailMediaInjectionCandidateParams {
  retailMediaEnabled: boolean | null | undefined;
  retailMediaBlocked: boolean;
  locale: string | null | undefined;
  deviceType: string;
  hasLegacyRetailMediaApp: boolean;
  trackPageLayout: unknown;
}

export function isRetailMediaInjectionCandidate({
  retailMediaEnabled,
  retailMediaBlocked,
  locale,
  deviceType,
  hasLegacyRetailMediaApp,
  trackPageLayout,
}: RetailMediaInjectionCandidateParams): boolean {
  const [language, country] = (locale ?? '').split(/[_-]/);

  return (
    retailMediaEnabled === true &&
    !retailMediaBlocked &&
    INJECTION_LOCALES.has(`${language}_${country}`.toLowerCase()) &&
    deviceType === 'mobile' &&
    !hasLegacyRetailMediaApp &&
    canInjectRetailMedia(trackPageLayout)
  );
}
